import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmptyState } from "@/components/common";
import { toast } from "sonner";
import { fmtUSD } from "@/lib/format";
import { CalendarClock, Repeat, Play, Trash2, Plus } from "lucide-react";

const ordinal = (n) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

export const AutoInvestPlans = ({ kind = "invest", portfolios = [], onChanged }) => {
  const [plans, setPlans] = useState([]);
  const [creating, setCreating] = useState(false);
  const [portfolioId, setPortfolioId] = useState("");
  const [amount, setAmount] = useState(250);
  const [day, setDay] = useState(1);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/invest/plans?kind=${kind}`); setPlans(data); }
    catch (e) { toast.error("Could not load your recurring plans"); }
  }, [kind]);
  useEffect(() => { load(); }, [load]);

  const available = portfolios.filter((p) => !plans.some((pl) => pl.portfolio_id === p.id));

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/invest/plans", { portfolio_id: portfolioId, amount_usd: Number(amount), day_of_month: Number(day) });
      toast.success("Recurring plan created", { description: `${fmtUSD(amount)} on the ${ordinal(Number(day))} of each month` });
      setCreating(false); setPortfolioId("");
      await load();
    } catch (e) { toast.error("Could not create plan", { description: e?.response?.data?.detail }); }
    finally { setBusy(false); }
  };

  const patch = async (id, body) => {
    try { await api.patch(`/invest/plans/${id}`, body); await load(); }
    catch (e) { toast.error("Could not update plan", { description: e?.response?.data?.detail }); }
  };

  const remove = async (id) => {
    try { await api.delete(`/invest/plans/${id}`); setPlans((p) => p.filter((x) => x.id !== id)); toast.success("Plan cancelled"); }
    catch (e) { toast.error("Could not cancel plan"); }
  };

  const runNow = async (id) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/invest/plans/${id}/run`);
      if (data.run.status === "invested") {
        toast.success("Contribution invested", { description: `${fmtUSD(data.run.amount_usd)} added to ${data.run.portfolio_name}` });
      } else {
        toast.warning("Contribution skipped", { description: data.run.reason || "Not enough USD in your wallet" });
      }
      await load();
      if (onChanged) await onChanged();
    } catch (e) { toast.error("Could not run the plan now"); }
    finally { setBusy(false); }
  };

  return (
    <Card className="p-5 shadow-premium" data-testid="auto-invest-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 text-sm font-medium"><Repeat className="h-4 w-4" />Recurring auto-invest</div>
        {!creating && (available.length > 0 ? (
          <Button size="sm" variant="secondary" className="gap-1.5" onClick={() => { setCreating(true); setPortfolioId(available[0].id); }}
            data-testid="auto-invest-new">
            <Plus className="h-3.5 w-3.5" />New plan
          </Button>
        ) : plans.length > 0 && (
          <span className="text-[11px] text-muted-foreground" data-testid="auto-invest-all-covered">
            One plan per portfolio
          </span>
        ))}
      </div>

      {creating && (
        <div className="rounded-xl border p-4 space-y-3 mb-4" data-testid="auto-invest-form">
          <div className="space-y-1.5">
            <Label className="text-xs">Portfolio</Label>
            <Select value={portfolioId} onValueChange={setPortfolioId}>
              <SelectTrigger data-testid="auto-invest-portfolio"><SelectValue placeholder="Choose a portfolio" /></SelectTrigger>
              <SelectContent>
                {available.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Monthly amount (USD)</Label>
              <Input type="number" min={10} value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="auto-invest-amount" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Payday</Label>
              <Select value={String(day)} onValueChange={setDay}>
                <SelectTrigger data-testid="auto-invest-day"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                    <SelectItem key={d} value={String(d)}>{ordinal(d)} of the month</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground">
            We sweep {fmtUSD(Number(amount) || 0)} from your USD wallet on the {ordinal(Number(day))} and buy your portfolio's mix.
            If the wallet is short we skip that month and tell you — nothing is ever sold.
          </p>
          <div className="flex gap-2">
            <Button size="sm" onClick={create} disabled={busy || !portfolioId} data-testid="auto-invest-submit">
              {busy ? "Saving..." : "Start plan"}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setCreating(false)} data-testid="auto-invest-cancel">Cancel</Button>
          </div>
        </div>
      )}

      {plans.length === 0 && !creating ? (
        <EmptyState title="No recurring plans yet"
          hint={portfolios.length ? "Set an amount and a payday — we'll invest it for you every month." : "Fund a portfolio first, then automate it."}
          testid="auto-invest-empty" />
      ) : (
        <div className="space-y-2.5" data-testid="auto-invest-list">
          {plans.map((p) => (
            <div key={p.id} className="rounded-xl border p-3.5" data-testid={`auto-invest-plan-${p.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm truncate">{p.portfolio_name}</span>
                    <Badge variant={p.active ? "default" : "secondary"} className="text-[10px]">
                      {p.active ? "Active" : "Paused"}
                    </Badge>
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {fmtUSD(p.amount_usd)} · {ordinal(p.day_of_month)} of each month
                  </div>
                </div>
                <Switch checked={p.active} onCheckedChange={(v) => patch(p.id, { active: v })}
                  data-testid={`auto-invest-toggle-${p.id}`} />
              </div>
              <div className="grid grid-cols-3 gap-2 mt-3 text-center">
                <div className="rounded-lg bg-secondary/60 py-2">
                  <div className="text-[10px] text-muted-foreground uppercase tracking-wide">Contributed</div>
                  <div className="numeric text-sm font-semibold">{fmtUSD(p.total_contributed)}</div>
                </div>
                <div className="rounded-lg bg-secondary/60 py-2">
                  <div className="text-[10px] text-muted-foreground uppercase tracking-wide">Runs</div>
                  <div className="numeric text-sm font-semibold">{p.runs}</div>
                </div>
                <div className="rounded-lg bg-secondary/60 py-2">
                  <div className="text-[10px] text-muted-foreground uppercase tracking-wide">Next</div>
                  <div className="numeric text-sm font-semibold">{p.active ? p.next_run : "—"}</div>
                </div>
              </div>
              {p.last_status === "skipped" && (
                <p className="text-[11px] text-amber-600 mt-2" data-testid={`auto-invest-skipped-${p.id}`}>
                  Last run was skipped — your USD wallet was short.
                </p>
              )}
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="secondary" className="gap-1.5" disabled={busy} onClick={() => runNow(p.id)}
                  data-testid={`auto-invest-run-${p.id}`}>
                  <Play className="h-3.5 w-3.5" />Run now
                </Button>
                <Button size="sm" variant="ghost" className="gap-1.5 text-muted-foreground" onClick={() => remove(p.id)}
                  data-testid={`auto-invest-delete-${p.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />Cancel
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};
